import { server_url } from "@script/config";
import type * as type from "@vTypes";

export async function upload(formData: FormData) {
  try {
    const response = await fetch(`${server_url}/nurses/upload-excel`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "업로드에 실패했습니다.");
    }
    const result = await response.json();
    return result;
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error ? error.message : "업로드에 실패했습니다.");
    throw new Error(msg);
  }
}

export async function confirm_upload(
  excelData: type.FullNurseSchema[],
  checklist: boolean[],
  newGroup: string[]
) {
  try {
    const response = await fetch(`${server_url}/nurses/confirm-upload`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        data: excelData,
        include_rows: checklist,
        new_groups_to_create: newGroup,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "업로드에 실패했습니다.");
    }
    const result = await response.json();
    return result;
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error ? error.message : "업로드에 실패했습니다.");
    throw new Error(msg);
  }
}

export async function template_download() {
  try {
    const response = await fetch(`${server_url}/nurses/template-download`, {
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("템플릿 다운로드에 실패했습니다.");
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "간호사_정보_템플릿.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error
        ? error.message
        : "템플릿 다운로드에 실패했습니다.");
    throw new Error(msg);
  }
}

// 엑셀 업로드2 템플릿 다운로드 (계정ID/이름 2컬럼)
export async function template_download2() {
  try {
    const response = await fetch(`${server_url}/nurses/template2-download`, {
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("템플릿 다운로드에 실패했습니다.");
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "간호사_업로드2_템플릿.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error
        ? (error as Error).message
        : "템플릿 다운로드에 실패했습니다.");
    throw new Error(msg);
  }
}

// 엑셀 업로드2 실행
// 엑셀 업로드2 실행 (ADM의 경우 group_id를 쿼리로 전달)
export async function upload2(formData: FormData, groupId?: string) {
  try {
    const base = `${server_url}/nurses/upload2-excel`;
    const inferredGid =
      groupId ??
      (typeof window !== "undefined"
        ? window.localStorage.getItem("hm_selected_group") ||
          window.localStorage.getItem("selectedGroupId") ||
          undefined
        : undefined);
    const url =
      inferredGid && inferredGid !== "null" && inferredGid.trim() !== ""
        ? `${base}?group_id=${encodeURIComponent(inferredGid)}`
        : base;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "업로드2에 실패했습니다.");
    }
    const result = await response.json();
    return result;
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error ? error.message : "업로드2에 실패했습니다.");
    throw new Error(msg);
  }
}

// 업로드2: 1) 검증 단계 (multipart/form-data)
export async function upload2_validate(formData: FormData, group_id?: string) {
  try {
    const query = group_id ? `?group_id=${encodeURIComponent(group_id)}` : "";
    const response = await fetch(
      `${server_url}/nurses/upload2-validate?group_id=${query}`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "검증에 실패했습니다.");
    }
    return await response.json();
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error ? error.message : "검증에 실패했습니다.");
    throw new Error(msg);
  }
}

// 업로드2: 2) 확정 단계 (application/json)
export async function upload2_confirm(
  rows: unknown[],
  groupId?: string | null
) {
  try {
    const payload: Record<string, unknown> = { rows };
    if (groupId && groupId.trim() !== "" && groupId !== "null") {
      payload.group_id = groupId;
    }
    const response = await fetch(`${server_url}/nurses/upload2-confirm`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "저장에 실패했습니다.");
    }
    return await response.json();
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error ? error.message : "저장에 실패했습니다.");
    throw new Error(msg);
  }
}

export async function schedule_download(
  schedule_id: string,
  year: number,
  month: number,
  groupId?: string,
  groupByTeam?: boolean
) {
  try {
    const base = `${server_url}/roster/schedule/${schedule_id}/export`;
    const inferredGid =
      groupId ??
      (typeof window !== "undefined"
        ? window.localStorage.getItem("selectedGroupId") || undefined
        : undefined);
    const params = new URLSearchParams();
    if (inferredGid) params.set("group_id", inferredGid);
    if (groupByTeam) params.set("group_by_team", "true");
    // 캐시버스트: CloudFront/브라우저가 이전 응답(304)을 재생해 다운로드가 깨지는 것 방지.
    params.set("_", String(Date.now()));
    const qs = params.toString();
    const url = qs ? `${base}?${qs}` : base;
    const response = await fetch(url, { credentials: "include", cache: "no-store" });
    if (!response.ok) {
      throw new Error("근무표 다운로드에 실패했습니다.");
    }
    const teamView = response.headers.get("X-Roster-TeamView") === "1";
    // 병동명은 서버가 정본이다(schedule.group_id 로 조회). HTTP 헤더가 latin-1 만 담아
    // 퍼센트 인코딩돼 오므로 여기서 푼다. 못 읽으면 병동명 없이 예전 이름 그대로 내려간다.
    let wardName = "";
    try {
      wardName = decodeURIComponent(
        response.headers.get("X-Roster-GroupName") || ""
      ).trim();
    } catch {
      wardName = "";
    }
    // 파일명에 못 쓰는 문자는 브라우저·OS 가 제각각 처리하므로 미리 없앤다.
    const safeWard = wardName.replace(/[\\/:*?"<>|]/g, "_");
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `${safeWard ? `${safeWard}_` : ""}${year}년_${month}월_근무표_다운로드${
      teamView ? "_팀별보기" : ""
    }.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error
        ? error.message
        : "근무표 다운로드에 실패했습니다.");
    throw new Error(msg);
  }
}

export async function upload_mworks_division(formData: FormData) {
  try {
    const response = await fetch(`${server_url}/setting/division_upload`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    console.log("response : ", response);
    // if (!response.ok) {
    //     const errorData = await response.json();
    //     throw new Error(errorData.detail || '업로드에 실패했습니다111.');
    // }
    const result = await response.json();
    return result;
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error ? error.message : "업로드에 실패했습니다.222");
    throw new Error(msg);
  }
}
export async function upload_mworks_position(formData: FormData) {
  try {
    const response = await fetch(`${server_url}/setting/position_upload`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    console.log("response : ", response);
    // if (!response.ok) {
    //     const errorData = await response.json();
    //     throw new Error(errorData.detail || '업로드에 실패했습니다111.');
    // }
    const result = await response.json();
    return result;
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error ? error.message : "업로드에 실패했습니다.222");
    throw new Error(msg);
  }
}

export async function upload_mworks_member(formData: FormData) {
  try {
    const response = await fetch(`${server_url}/setting/member_upload`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    const result = await response.json();

    // 1) 성공한 경우 (result: "succeed")
    if (result?.result === "succeed") {
      return { result: "succeed" };
    }

    // 2) 오류 배열인 경우 (백엔드에서 array 반환)
    if (Array.isArray(result)) {
      // 그대로 반환 → 프론트에서 table로 렌더링 가능
      return result;
    }

    // 3) 기타 실패 메시지
    return { result: "fail", message: result?.result || "unknown_error" };
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error ? error.message : "업로드에 실패했습니다.");
    throw new Error(msg);
  }
}

// export async function upload_mworks_member(formData: FormData) {
//   try {
//     const response = await fetch(`${server_url}/setting/member_upload`, {
//       method: "POST",
//       credentials: "include",
//       body: formData,
//     });

//     console.log("response : ", response);
//     // if (!response.ok) {
//     //     const errorData = await response.json();
//     //     throw new Error(errorData.detail || '업로드에 실패했습니다111.');
//     // }
//     const result = await response.json();
//     return result;
//   } catch (error: unknown) {
//     const msg =
//       (error as { detail?: string })?.detail ||
//       (error instanceof Error ? error.message : "업로드에 실패했습니다.222");
//     throw new Error(msg);
//   }
// }

export async function template_download_mworks_division() {
  try {
    const response = await fetch(
      `${server_url}/setting/division_download/easysetting_division.xls`,
      {
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error("템플릿 다운로드에 실패했습니다.");
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "부서_등록_템플릿.xls";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error
        ? error.message
        : "템플릿 다운로드에 실패했습니다.");
    throw new Error(msg);
  }
}

export async function template_download_mworks_position() {
  try {
    const response = await fetch(
      `${server_url}/setting/position_download/easysetting_position.xls`,
      {
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error("템플릿 다운로드에 실패했습니다.");
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "직위_등록_템플릿.xls";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error
        ? error.message
        : "템플릿 다운로드에 실패했습니다.");
    throw new Error(msg);
  }
}

export async function template_download_mworks_member() {
  try {
    const response = await fetch(
      `${server_url}/setting/member_download/easysetting_member.xls`,
      {
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error("템플릿 다운로드에 실패했습니다.");
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "직원_등록_템플릿.xls";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    const msg =
      (error as { detail?: string })?.detail ||
      (error instanceof Error
        ? error.message
        : "템플릿 다운로드에 실패했습니다.");
    throw new Error(msg);
  }
}
